import type { PayloadAction } from "@reduxjs/toolkit";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { matchPath } from "react-router-dom";
import apiUrls from "../../../api";
import type { RootState } from "../../../common/state/store";
import { queryVisibleAnchor } from "../engine/waitForAnchor";
import { enqueueOnboardingEvent } from "../events/eventQueue";
import {
  getBranchingParent,
  getChecklistForAudience,
  getStepPosition,
  getTour,
  getToursForAudience,
  isAwaitStep,
} from "../registry";
import type {
  ChecklistDefinition,
  ChecklistItem,
  EventSurface,
  TourDefinition,
} from "../registry/types";
import { applyOps } from "./applyOps";
import type { Audience, OnboardingOp, OnboardingState, TourStatus } from "./onboardingTypes";
import { getAllSessionsByProjectGroup } from "../../stats/state/statsSlice";
import { invalidateCreatorStatsCache } from "../../statsNew/state/statsNewSlice";

type OnboardingFetchPayload = { audience: Audience; state: OnboardingState };

type ActiveTour = {
  id: string;
  stepIndex: number;
  source: EventSurface;
  startAnnounced: boolean;
  pathname: string | null;
  routeGate: string | null;
  resumed: boolean;
  anyStepShown: boolean;
  resumePoint: { stepIndex: number; pathname: string } | null;
  awaitRouteIndex: number;
  positionOffset: StepPositionOffset;
  inPlace: boolean;
  autoReshown: boolean;
  branchParentId: string | null;
  rejoined: boolean;
  routeReached: boolean;
  replay: boolean;
  skippedSteps: number[];
  viewedSteps: number[];
};

export type StepPositionOffset = { before: number; after: number };

export type StartTourPayload = {
  id: string;
  source: EventSurface;
  stepIndex?: number;
  route?: string;
  positionOffset?: StepPositionOffset;
  inPlace?: boolean;
  autoReshown?: boolean;
  branchParentId?: string;
  rejoined?: boolean;
  replay?: boolean;
};

export type DismissWatch = {
  id: string;
  source: EventSurface;
  fromStep: number;
  visibleAtDismiss: string[];
};

export type ResumableTour = {
  id: string;
  step: number;
  route: string;
  updatedAtMs: number;
};

export const RESUME_WINDOW_MS = 12 * 60 * 60 * 1000;

export const SESSION_STARTED_AT_MS = Date.now();

export type PendingTour = {
  id: string;
  source: EventSurface;
  stepsBefore?: number;
  stepIndex?: number;
  focusCourseId?: number;
  focusLatestCourse?: boolean;
  replay?: boolean;
};

export type OnboardingSliceState = {
  status: "idle" | "loading" | "ready" | "error";
  audience: Audience | null;
  remote: OnboardingState | null;
  activeTour: ActiveTour | null;
  pendingTour: PendingTour | null;
  locationHintTourId: string | null;
  dismissWatch: DismissWatch | null;
  welcomeOpen: boolean;
  demoCleanupPromptOpen: boolean;
  latestApplyOpsRequestId: string | null;
};

const initialState: OnboardingSliceState = {
  status: "idle",
  audience: null,
  remote: null,
  activeTour: null,
  pendingTour: null,
  locationHintTourId: null,
  dismissWatch: null,
  welcomeOpen: false,
  demoCleanupPromptOpen: false,
  latestApplyOpsRequestId: null,
};

export const fetchOnboardingState = createAsyncThunk<OnboardingFetchPayload>(
  "onboarding/fetchState",
  async () => {
    const response = await axios.get<OnboardingFetchPayload>(apiUrls.onboarding.state);
    return response.data;
  },
);

let applyOpsQueue: Promise<unknown> = Promise.resolve();

export const applyOnboardingOps = createAsyncThunk<OnboardingFetchPayload, OnboardingOp[]>(
  "onboarding/applyOps",
  async (ops, { dispatch, rejectWithValue }) => {
    const request = applyOpsQueue.then(() =>
      axios.post<OnboardingFetchPayload>(apiUrls.onboarding.state, { ops }),
    );
    applyOpsQueue = request.catch(() => undefined);
    try {
      const response = await request;
      return response.data;
    } catch (err) {
      void dispatch(fetchOnboardingState());
      if (axios.isAxiosError(err)) {
        return rejectWithValue(err.response?.data);
      }
      throw err;
    }
  },
);

export const resetOnboardingState = createAsyncThunk<OnboardingFetchPayload>(
  "onboarding/reset",
  async () => {
    const response = await axios.post<OnboardingFetchPayload>(apiUrls.onboarding.reset);
    return response.data;
  },
);

export type SampleCourseSurface = Extract<EventSurface, "empty_state" | "checklist">;

export const cloneSampleCourse = createAsyncThunk<
  { projectGroupId: number },
  { surface: SampleCourseSurface }
>("onboarding/cloneSampleCourse", async ({ surface }, { dispatch, rejectWithValue }) => {
  try {
    const response = await axios.post<{ projectGroupId: number }>(apiUrls.onboarding.sampleCourse, {
      surface,
    });
    await dispatch(fetchOnboardingState());
    return response.data;
  } catch (err) {
    void dispatch(fetchOnboardingState());
    if (axios.isAxiosError(err)) {
      return rejectWithValue(err.response?.data);
    }
    throw err;
  }
});

export type SampleStatsSurface = Extract<
  EventSurface,
  "empty_state" | "checklist" | "management" | "tour"
>;

export const createSampleStats = createAsyncThunk<
  | { projectGroupId: number; sessionCount: number }
  | { alreadyExists: true }
  | { skippedRealSessions: true },
  { surface: SampleStatsSurface }
>("onboarding/createSampleStats", async ({ surface }, { dispatch, rejectWithValue }) => {
  try {
    const response = await axios.post<
      | { projectGroupId: number; sessionCount: number }
      | { alreadyExists: true }
      | { skippedRealSessions: true }
    >(apiUrls.onboarding.sampleStats, { surface });
    await dispatch(fetchOnboardingState());
    return response.data;
  } catch (err) {
    if (axios.isAxiosError(err)) {
      return rejectWithValue(err.response?.data);
    }
    throw err;
  }
});

export const createDemoBuild = createAsyncThunk<
  { buildId: number; alreadyExists?: boolean },
  { projectGroupId: number }
>("onboarding/createDemoBuild", async ({ projectGroupId }, { dispatch, rejectWithValue }) => {
  try {
    const response = await axios.post<{ buildId: number; alreadyExists?: boolean }>(
      apiUrls.onboarding.demoBuild,
      { projectGroupId },
    );
    await dispatch(fetchOnboardingState());
    return response.data;
  } catch (err) {
    if (axios.isAxiosError(err)) {
      return rejectWithValue(err.response?.data);
    }
    throw err;
  }
});

export const cleanupDemoData = createAsyncThunk<{
  deletedSessions: number;
  deletedSubdata: number;
  deletedBuilds: number;
}>("onboarding/cleanupDemoData", async (_, { dispatch, rejectWithValue }) => {
  try {
    const response = await axios.post<{
      deletedSessions: number;
      deletedSubdata: number;
      deletedBuilds: number;
    }>(apiUrls.onboarding.demoDataCleanup);
    await dispatch(fetchOnboardingState());
    return response.data;
  } catch (err) {
    if (axios.isAxiosError(err)) {
      return rejectWithValue(err.response?.data);
    }
    throw err;
  }
});

export const onboardingSlice = createSlice({
  name: "onboarding",
  initialState,
  reducers: {
    openDemoCleanupPrompt: (state: OnboardingSliceState) => {
      state.demoCleanupPromptOpen = true;
    },
    closeDemoCleanupPrompt: (state: OnboardingSliceState) => {
      state.demoCleanupPromptOpen = false;
    },
    startTour: (state: OnboardingSliceState, action: PayloadAction<StartTourPayload>) => {
      const {
        id,
        source,
        stepIndex,
        route,
        positionOffset,
        inPlace,
        autoReshown,
        branchParentId,
        rejoined,
        replay,
      } = action.payload;
      const resumed = route !== undefined && !inPlace;
      state.activeTour = {
        id,
        stepIndex: stepIndex ?? 0,
        source,
        startAnnounced: false,
        pathname: null,
        routeGate: route ?? getTour(id, state.audience)?.startRoute ?? null,
        resumed,
        anyStepShown: route !== undefined,
        resumePoint: null,
        awaitRouteIndex: -1,
        positionOffset: positionOffset ?? { before: 0, after: 0 },
        inPlace: Boolean(inPlace),
        autoReshown: Boolean(autoReshown),
        branchParentId: branchParentId ?? null,
        rejoined: Boolean(rejoined),
        routeReached: false,
        replay: Boolean(replay) || state.remote?.tours[id]?.status === "completed",
        skippedSteps: [],
        viewedSteps: [],
      };
      state.pendingTour = null;
      state.dismissWatch = null;
      state.locationHintTourId = null;
    },
    setDismissWatch: (state: OnboardingSliceState, action: PayloadAction<DismissWatch | null>) => {
      state.dismissWatch = action.payload;
    },
    markTourStartAnnounced: (state: OnboardingSliceState) => {
      if (state.activeTour) {
        state.activeTour.startAnnounced = true;
      }
    },
    markStepSkipped: (state: OnboardingSliceState, action: PayloadAction<number>) => {
      if (
        state.activeTour &&
        !state.activeTour.skippedSteps.includes(action.payload) &&
        !state.activeTour.viewedSteps.includes(action.payload)
      ) {
        state.activeTour.skippedSteps.push(action.payload);
      }
    },
    markStepViewed: (state: OnboardingSliceState, action: PayloadAction<number>) => {
      if (state.activeTour && !state.activeTour.viewedSteps.includes(action.payload)) {
        state.activeTour.viewedSteps.push(action.payload);
      }
    },
    markTourRouteReached: (state: OnboardingSliceState) => {
      if (state.activeTour) {
        state.activeTour.routeReached = true;
      }
    },
    setPendingTour: (state: OnboardingSliceState, action: PayloadAction<PendingTour | null>) => {
      state.pendingTour = action.payload;
      if (action.payload) {
        state.locationHintTourId = null;
      }
    },
    setLocationHintTour: (state: OnboardingSliceState, action: PayloadAction<string | null>) => {
      state.locationHintTourId = action.payload;
    },
    goToStep: (state: OnboardingSliceState, action: PayloadAction<number>) => {
      if (state.activeTour) {
        state.activeTour.stepIndex = action.payload;
        state.activeTour.awaitRouteIndex = -1;
      }
    },
    reachAwaitRoute: (state: OnboardingSliceState, action: PayloadAction<number>) => {
      if (state.activeTour && action.payload > state.activeTour.awaitRouteIndex) {
        state.activeTour.awaitRouteIndex = action.payload;
      }
    },
    checklistItemCredited: (state: OnboardingSliceState, action: PayloadAction<string>) => {
      const active = state.activeTour;
      if (!active) {
        return;
      }
      const tour = getTour(active.id, state.audience);
      const step = tour?.steps[active.stepIndex];
      if (!tour || !step) {
        return;
      }
      const awaitIndex = isAwaitStep(step) ? active.stepIndex : active.stepIndex + 1;
      const awaitStep = tour.steps[awaitIndex];
      if (
        !awaitStep ||
        !isAwaitStep(awaitStep) ||
        awaitStep.untilChecklistItem !== action.payload ||
        (awaitIndex !== active.stepIndex && (isAwaitStep(step) || !step.hint))
      ) {
        return;
      }
      if (awaitIndex >= tour.steps.length - 1) {
        return;
      }
      active.stepIndex = awaitIndex + 1;
      active.pathname = null;
      active.awaitRouteIndex = -1;
    },
    tourSignal: (state: OnboardingSliceState, action: PayloadAction<string>) => {
      const active = state.activeTour;
      if (!active) {
        return;
      }
      const tour = getTour(active.id, state.audience);
      const step = tour?.steps[active.stepIndex];
      if (!tour || !step) {
        return;
      }
      const awaitIndex = isAwaitStep(step) ? active.stepIndex : active.stepIndex + 1;
      const awaitStep = tour.steps[awaitIndex];
      const awaitMatches =
        awaitStep !== undefined &&
        isAwaitStep(awaitStep) &&
        awaitStep.untilSignal === action.payload &&
        (awaitIndex === active.stepIndex || (!isAwaitStep(step) && Boolean(step.hint)));
      if (awaitMatches && awaitIndex < tour.steps.length - 1) {
        active.stepIndex = awaitIndex + 1;
        active.pathname = null;
        active.awaitRouteIndex = -1;
        return;
      }
      if (
        isAwaitStep(step) ||
        step.advanceOn !== "signal" ||
        step.signal !== action.payload ||
        active.stepIndex >= tour.steps.length - 1
      ) {
        return;
      }
      active.stepIndex += 1;
      active.awaitRouteIndex = -1;
    },
    awaitStepInterrupted: (state: OnboardingSliceState, action: PayloadAction<string>) => {
      const active = state.activeTour;
      if (!active || active.stepIndex === 0) {
        return;
      }
      const tour = getTour(active.id, state.audience);
      const step = tour?.steps[active.stepIndex];
      const previous = tour?.steps[active.stepIndex - 1];
      if (
        !step ||
        !isAwaitStep(step) ||
        step.untilChecklistItem !== action.payload ||
        !previous ||
        isAwaitStep(previous)
      ) {
        return;
      }
      active.stepIndex -= 1;
      active.pathname = null;
      active.awaitRouteIndex = -1;
    },
    pinTourPathname: (state: OnboardingSliceState, action: PayloadAction<string | null>) => {
      if (!state.activeTour) {
        return;
      }
      state.activeTour.pathname = action.payload;
      const pinnedStep = getTour(state.activeTour.id, state.audience)?.steps[
        state.activeTour.stepIndex
      ];
      if (action.payload !== null) {
        state.activeTour.routeGate = null;
        state.activeTour.anyStepShown = true;
        if (pinnedStep && !isAwaitStep(pinnedStep) && pinnedStep.hint) {
          return;
        }
        state.activeTour.resumePoint = {
          stepIndex: state.activeTour.stepIndex,
          pathname: action.payload,
        };
      }
    },
    endTour: (state: OnboardingSliceState) => {
      state.activeTour = null;
    },
    openWelcome: (state: OnboardingSliceState) => {
      state.welcomeOpen = true;
    },
    closeWelcome: (state: OnboardingSliceState) => {
      state.welcomeOpen = false;
    },
    resetOnboardingSession: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(fetchOnboardingState.pending, (state) => {
      state.status = "loading";
    });
    builder.addCase(fetchOnboardingState.fulfilled, (state, { payload }) => {
      state.status = "ready";
      state.audience = payload.audience;
      state.remote = payload.state;
    });
    builder.addCase(fetchOnboardingState.rejected, (state) => {
      state.status = "error";
    });
    builder.addCase(applyOnboardingOps.pending, (state, action) => {
      state.latestApplyOpsRequestId = action.meta.requestId;
      if (state.remote) {
        state.remote = applyOps(state.remote, action.meta.arg, new Date().toISOString());
      }
    });
    builder.addCase(applyOnboardingOps.fulfilled, (state, { payload, meta }) => {
      if (meta.requestId !== state.latestApplyOpsRequestId) {
        return;
      }
      state.status = "ready";
      state.audience = payload.audience;
      state.remote = payload.state;
    });
    builder.addCase(resetOnboardingState.fulfilled, (state, { payload }) => {
      state.status = "ready";
      state.audience = payload.audience;
      state.remote = payload.state;
    });
  },
});

export const {
  startTour,
  markTourStartAnnounced,
  markTourRouteReached,
  markStepSkipped,
  markStepViewed,
  setPendingTour,
  setLocationHintTour,
  goToStep,
  reachAwaitRoute,
  checklistItemCredited,
  tourSignal,
  awaitStepInterrupted,
  pinTourPathname,
  endTour,
  setDismissWatch,
  openWelcome,
  closeWelcome,
  openDemoCleanupPrompt,
  closeDemoCleanupPrompt,
  resetOnboardingSession,
} = onboardingSlice.actions;

const findVisibleHintStep = (tour: TourDefinition, fromIndex: number): number | null => {
  for (let index = Math.max(0, fromIndex); index < tour.steps.length; index += 1) {
    const candidate = tour.steps[index];
    if (
      candidate &&
      !isAwaitStep(candidate) &&
      candidate.hint &&
      queryVisibleAnchor(candidate.anchor)
    ) {
      return index;
    }
  }
  return null;
};

const SAMPLE_STATS_TOUR_ID = "creator-stats";

const toSampleStatsSurface = (source: EventSurface): SampleStatsSurface =>
  source === "empty_state" || source === "checklist" || source === "management" ? source : "tour";

export const launchTour = createAsyncThunk<
  void,
  PendingTour,
  { state: { onboarding: OnboardingSliceState } }
>(
  "onboarding/launchTour",
  async ({ id, source, stepIndex, focusCourseId, replay }, { dispatch, getState }) => {
    if (id === SAMPLE_STATS_TOUR_ID) {
      const created = await dispatch(createSampleStats({ surface: toSampleStatsSurface(source) }));
      if (createSampleStats.rejected.match(created)) {
        dispatch(onboardingSlice.actions.setLocationHintTour(id));
        return;
      }
      if (!("skippedRealSessions" in created.payload)) {
        dispatch(invalidateCreatorStatsCache());
        await dispatch(getAllSessionsByProjectGroup({ force: true }));
      }
    }
    const replayExtra = replay ? { replay: true } : {};
    const pendingExtras = {
      ...(stepIndex !== undefined ? { stepIndex } : {}),
      ...(focusCourseId !== undefined ? { focusCourseId } : {}),
      ...replayExtra,
    };
    const { audience } = getState().onboarding;
    const tour = getTour(id, audience);
    const inPlaceStep = tour ? findVisibleHintStep(tour, 0) : null;
    if (inPlaceStep !== null) {
      dispatch(
        startTour({
          id,
          source,
          stepIndex: inPlaceStep,
          route: window.location.pathname,
          inPlace: true,
          ...replayExtra,
        }),
      );
      return;
    }
    if (tour && tour.startRoute === null) {
      const firstStep = tour.steps[0];
      const alreadyInPlace =
        (firstStep !== undefined &&
          !isAwaitStep(firstStep) &&
          queryVisibleAnchor(firstStep.anchor)) ||
        (tour.presenceAnchor !== undefined && queryVisibleAnchor(tour.presenceAnchor) !== null);
      const branchingParent = alreadyInPlace ? null : getBranchingParent(id, audience);
      if (branchingParent && !tour.bridgeTourId) {
        void dispatch(
          launchTour({
            id: branchingParent.id,
            source,
            stepIndex: branchingParent.stepIndex,
            ...(focusCourseId !== undefined ? { focusCourseId } : {}),
            ...replayExtra,
          }),
        );
        return;
      }
      const bridge =
        tour.bridgeTourId && !alreadyInPlace ? getTour(tour.bridgeTourId, audience) : undefined;
      if (bridge) {
        const bridgeTotal = getStepPosition(bridge, 0).total;
        const tourTotal = getStepPosition(tour, 0).total;
        dispatch(
          startTour({
            id: bridge.id,
            source,
            positionOffset: { before: 0, after: tourTotal },
            ...replayExtra,
          }),
        );
        dispatch(
          setPendingTour({
            id,
            source,
            stepsBefore: bridgeTotal,
            ...(focusCourseId === undefined ? { focusLatestCourse: true } : {}),
            ...pendingExtras,
          }),
        );
        return;
      }
      dispatch(setPendingTour({ id, source, ...pendingExtras }));
      return;
    }
    dispatch(startTour({ id, source, ...replayExtra }));
  },
);

export const findResumableTours = (
  remote: OnboardingState | null,
  audience: Audience | null,
  nowMs: number,
  sessionStartedAtMs: number = SESSION_STARTED_AT_MS,
): ResumableTour[] => {
  if (!remote || !audience) {
    return [];
  }
  const resumable: ResumableTour[] = [];
  for (const tour of getToursForAudience(audience)) {
    const entry = remote.tours[tour.id];
    const storedStep = entry ? tour.steps[entry.step] : undefined;
    if (!entry?.route || !storedStep || isAwaitStep(storedStep)) {
      continue;
    }
    const updatedAtMs = Date.parse(entry.updatedAt);
    if (!Number.isFinite(updatedAtMs) || nowMs - updatedAtMs >= RESUME_WINDOW_MS) {
      continue;
    }
    const isPaused = entry.status === "paused";
    const isStaleStarted = entry.status === "started" && updatedAtMs < sessionStartedAtMs;
    if (!isPaused && !isStaleStarted) {
      continue;
    }
    resumable.push({ id: tour.id, step: entry.step, route: entry.route, updatedAtMs });
  }
  return resumable.sort((a, b) => b.updatedAtMs - a.updatedAtMs);
};

export type SuspendedParent = { id: string; step: number };

export const findSuspendedParent = (
  remote: OnboardingState | null,
  audience: Audience | null,
  childId: string,
): SuspendedParent | null => {
  if (!remote || !audience) {
    return null;
  }
  for (const [id, entry] of Object.entries(remote.tours)) {
    if (entry.status !== "suspended") {
      continue;
    }
    const step = getTour(id, audience)?.steps[entry.step];
    if (step && !isAwaitStep(step) && step.branchOn?.tourId === childId) {
      return { id, step: entry.step };
    }
  }
  return null;
};

export type RejoinableTour = { id: string; stepIndex: number; anchor: string };

export const findRejoinableTours = (
  remote: OnboardingState | null,
  audience: Audience | null,
): RejoinableTour[] => {
  if (!remote || !audience) {
    return [];
  }
  const rejoinable: RejoinableTour[] = [];
  for (const tour of getToursForAudience(audience)) {
    const entry = remote.tours[tour.id];
    if (!entry || (entry.status !== "paused" && entry.status !== "suspended")) {
      continue;
    }
    const awaitIndex = tour.steps.findIndex(
      (step, index) => index >= entry.step && isAwaitStep(step),
    );
    const awaitStep = tour.steps[awaitIndex];
    const afterAwait = tour.steps[awaitIndex + 1];
    if (!awaitStep || !isAwaitStep(awaitStep) || !afterAwait || isAwaitStep(afterAwait)) {
      continue;
    }
    const doneAt = awaitStep.untilChecklistItem
      ? remote.checklist.items[awaitStep.untilChecklistItem]?.doneAt
      : undefined;
    if (!doneAt || Date.parse(doneAt) <= Date.parse(entry.updatedAt)) {
      continue;
    }
    rejoinable.push({ id: tour.id, stepIndex: awaitIndex + 1, anchor: afterAwait.anchor });
  }
  return rejoinable;
};

export type ChecklistProgress = {
  checklist: ChecklistDefinition;
  doneIds: Set<string>;
  doneCount: number;
  total: number;
  allDone: boolean;
  completedAtMs: number | null;
};

export const getChecklistProgress = (
  remote: OnboardingState | null,
  audience: Audience | null,
): ChecklistProgress | null => {
  if (!remote || !audience) {
    return null;
  }
  const checklist = getChecklistForAudience(audience);
  if (!checklist) {
    return null;
  }
  const doneAtById = new Map<string, string>();
  for (const item of checklist.items) {
    const doneAt =
      item.id === "welcome" ? remote.welcome.seenAt : remote.checklist.items[item.id]?.doneAt;
    if (doneAt) {
      doneAtById.set(item.id, doneAt);
    }
  }
  const total = checklist.items.length;
  const doneCount = doneAtById.size;
  const allDone = total > 0 && doneCount === total;
  const completedAtMs = allDone
    ? Math.max(...Array.from(doneAtById.values()).map((doneAt) => Date.parse(doneAt)))
    : null;
  return {
    checklist,
    doneIds: new Set(doneAtById.keys()),
    doneCount,
    total,
    allDone,
    completedAtMs: completedAtMs !== null && Number.isFinite(completedAtMs) ? completedAtMs : null,
  };
};

export const findChecklistItemForTour = (
  progress: ChecklistProgress | null,
  tourId: string,
): ChecklistItem | null => {
  if (!progress) {
    return null;
  }
  const linkedTourIds = [tourId, getBranchingParent(tourId, null)?.id].filter(
    (candidate): candidate is string => candidate !== undefined,
  );
  const linked = progress.checklist.items.filter(
    (item) => item.tourId !== undefined && linkedTourIds.includes(item.tourId),
  );
  return linked.find((item) => !progress.doneIds.has(item.id)) ?? linked[0] ?? null;
};

export const findResumableForItem = (
  candidates: ResumableTour[],
  item: ChecklistItem,
): ResumableTour | undefined =>
  candidates.find(
    (candidate) =>
      candidate.id === item.tourId || getBranchingParent(candidate.id, null)?.id === item.tourId,
  );

export const skipTour = createAsyncThunk<
  void,
  { id: string; step: number },
  { state: { onboarding: OnboardingSliceState } }
>("onboarding/skipTour", ({ id, step }, { dispatch, getState }) => {
  const { remote, audience } = getState().onboarding;
  dispatch(setDismissWatch(null));
  const parent = findSuspendedParent(remote, audience, id);
  const dropped = [{ id, step }, ...(parent ? [parent] : [])];
  void dispatch(
    applyOnboardingOps(
      dropped.map((tour) => ({
        op: "tourProgress" as const,
        id: tour.id,
        status: "skipped" as const,
        step: tour.step,
      })),
    ),
  );
  for (const tour of dropped) {
    enqueueOnboardingEvent({
      event: "tour_skipped",
      surface: "tour",
      itemId: tour.id,
      step: tour.step,
      meta: { reason: "explicit" },
    });
  }
});

type LoadedEntities = {
  projectGroups?: { current?: { id?: number | string } };
  projects?: { currentProject?: { id?: number | string } };
};

export const courseIdFromRoute = (route: string): number | undefined => {
  const courseMatch = matchPath("/courses/:id/*", route);
  const id = Number(courseMatch?.params.id);
  return Number.isInteger(id) && id > 0 ? id : undefined;
};

export const routeNeedsUnloadedEntity = (route: string, loaded: LoadedEntities): boolean => {
  const courseMatch = matchPath("/courses/:id/*", route);
  if (!courseMatch || courseMatch.params.id === "new") {
    return false;
  }
  if (Number(loaded.projectGroups?.current?.id) !== Number(courseMatch.params.id)) {
    return true;
  }
  const moduleMatch =
    matchPath("/courses/:id/list/:projectId/*", route) ??
    matchPath("/courses/:id/list/:projectId", route);
  return (
    moduleMatch !== null &&
    Number(loaded.projects?.currentProject?.id) !== Number(moduleMatch.params.projectId)
  );
};

export const resumeTour = createAsyncThunk<
  void,
  PendingTour,
  { state: { onboarding: OnboardingSliceState } & LoadedEntities }
>("onboarding/resumeTour", ({ id, source }, { dispatch, getState }) => {
  const { remote, audience } = getState().onboarding;
  const resumable = findResumableTours(remote, audience, Date.now()).find(
    (candidate) => candidate.id === id,
  );
  if (!resumable) {
    void dispatch(launchTour({ id, source }));
    return;
  }
  const tour = getTour(id, audience);
  const storedStep = tour?.steps[resumable.step];
  const storedStepHere =
    storedStep !== undefined && !isAwaitStep(storedStep) && queryVisibleAnchor(storedStep.anchor);
  const stepHere = storedStepHere
    ? resumable.step
    : tour
    ? findVisibleHintStep(tour, resumable.step)
    : null;
  if (stepHere !== null) {
    dispatch(startTour({ id, source, stepIndex: stepHere, route: window.location.pathname }));
    return;
  }
  const parent = findSuspendedParent(remote, audience, id);
  const branchingParent = parent ? null : getBranchingParent(id, audience);
  if (branchingParent) {
    void dispatch(applyOnboardingOps([{ op: "tourClear", id }]));
    void dispatch(
      launchTour({
        id: branchingParent.id,
        source,
        stepIndex: branchingParent.stepIndex,
        focusCourseId: courseIdFromRoute(resumable.route),
      }),
    );
    return;
  }
  if (parent) {
    void dispatch(applyOnboardingOps([{ op: "tourClear", id }]));
    if (routeNeedsUnloadedEntity(resumable.route, getState())) {
      void dispatch(
        launchTour({
          id: parent.id,
          source,
          stepIndex: parent.step,
          focusCourseId: courseIdFromRoute(resumable.route),
        }),
      );
      return;
    }
    const parentStep = getTour(parent.id, audience)?.steps[parent.step];
    const parentStepHere =
      parentStep !== undefined && !isAwaitStep(parentStep) && queryVisibleAnchor(parentStep.anchor);
    dispatch(
      startTour({
        id: parent.id,
        source,
        stepIndex: parent.step,
        route: parentStepHere ? window.location.pathname : resumable.route,
      }),
    );
    return;
  }
  if (routeNeedsUnloadedEntity(resumable.route, getState())) {
    void dispatch(
      launchTour({
        id,
        source,
        stepIndex: resumable.step,
        focusCourseId: courseIdFromRoute(resumable.route),
      }),
    );
    return;
  }
  dispatch(startTour({ id, source, stepIndex: resumable.step, route: resumable.route }));
});

export const onboardingReducer = onboardingSlice.reducer;

export const selectOnboardingStatus = (state: RootState) => state.onboarding.status;
export const selectOnboardingAudience = (state: RootState) => state.onboarding.audience;
export const selectOnboardingRemote = (state: RootState) => state.onboarding.remote;
export const selectSuggestionsMuted = (state: RootState) =>
  Boolean(state.onboarding.remote?.suggestions?.mutedAt);
export const selectActiveTour = (state: RootState) => state.onboarding.activeTour;
export const selectPendingTour = (state: RootState) => state.onboarding.pendingTour;
export const selectDismissWatch = (state: RootState) => state.onboarding.dismissWatch;
export const selectPendingTourId = (state: RootState) => state.onboarding.pendingTour?.id ?? null;
export const selectLocationHintTourId = (state: RootState) =>
  state.onboarding.pendingTour?.id ?? state.onboarding.locationHintTourId;
export const selectWelcomeOpen = (state: RootState) => state.onboarding.welcomeOpen;
export const selectDemoCleanupPromptOpen = (state: RootState) =>
  state.onboarding.demoCleanupPromptOpen;
export const selectTourStatus =
  (id: string) =>
  (state: RootState): TourStatus | undefined =>
    state.onboarding.remote?.tours[id]?.status;
